import { useEffect, useMemo, useRef, useState } from 'react'
import type { SpecMachine, SpecMachineItem } from '../../types'
import AddTagsDialog from './AddTagsDialog'
import DupCodes from './DupCodes'
import FindReplace from './FindReplace'
import type { FindMode } from './FindReplace'
import { selectionLabel, toFindSelection } from './findScope'
import type { FindHit, FindSelection } from './findScope'
import ItemGrid from './ItemGrid'
import type { ItemGridHandle } from './ItemGrid'
import type { MasterIssues, TagLookup } from './masterView'
import { fillFromTags, renamePrefix } from './masterView'
import { newKey } from './model'
import { ViewerAvatars, ViewerChips } from './Presence'
import { cellMarks } from './presenceMarks'
import SaveBadge from './SaveBadge'
import SheetStatus from './SheetStatus'
import type { CellNote } from './SheetStatus'
import { blankMachine } from './specDoc'
import { moveDevice } from './specOps'
import TabMenu from './TabMenu'
import type { SheetCell } from './useSheetTabs'
import { useSheetTabs } from './useSheetTabs'
import type { SpecDraft } from './useSpecDraft'

// 항목 편집 — 인터페이스 항목 시트(전송코드·설명·디바이스·주소)만 화면 가득, 아래 탭 = 인터페이스. 태그 편집 모드와 같은 짜임:
// 탭은 Alt+↑/↓ 이동(useSheetTabs)·더블클릭 ID 바꾸기·우클릭 메뉴·끌어서 순서 변경·[+] 추가, 시트 아래 상태 줄(SheetStatus).
// 찾기/바꾸기(Ctrl+F)도 태그 편집과 같다 — 전송코드·설명·태그 주소 칸, 이 인터페이스·모든 인터페이스·선택 영역.

/** 항목 편집을 열 칸 (검색 결과) */
export interface ItemStart { row: number; field: string }

type ItemText = 'code' | 'description' | 'address'
const ITEM_TEXT: readonly ItemText[] = ['code', 'description', 'address']
const FIND_COLUMNS = [
  { key: 'all', label: '모든 열' }, { key: 'code', label: '전송코드' }, { key: 'description', label: '설명' }, { key: 'address', label: '태그 주소' },
]

export default function MasterEditMode({ spec, machineKey, start, lookup, issues, onClose, onNotice }: {
  spec: SpecDraft
  machineKey: string | null
  start?: ItemStart | null
  lookup: TagLookup
  issues: MasterIssues
  onClose: () => void
  onNotice: (message: string) => void
}) {
  const { draft, setDraft, locate, viewers, me } = spec
  const machines = draft.machines
  const [activeKey, setActiveKey] = useState<string | null>(
    (machineKey && machines.some(m => m.key === machineKey) ? machineKey : null) ?? machines[0]?.key ?? null)
  const activeIndex = Math.max(0, machines.findIndex(m => m.key === activeKey))
  const active = machines[activeIndex] ?? null
  const others = useMemo(() => viewers.filter(v => v.connectionId !== me), [viewers, me])
  const remote = useMemo(() => cellMarks(others.filter(v => v.cursor?.device === active?.key), 'items', v => v.cursor?.tag), [others, active?.key])

  const activeMachineKey = active?.key ?? null
  useEffect(() => { locate({ mode: 'items', device: activeMachineKey }) }, [locate, activeMachineKey])
  useEffect(() => () => locate(null), [locate])

  const sheetRef = useRef<HTMLDivElement>(null)
  const [sheetHeight, setSheetHeight] = useState(400)
  useEffect(() => {
    const el = sheetRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setSheetHeight(Math.max(160, Math.floor(entry.contentRect.height))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // 전체 화면 동안 뒤쪽 페이지 스크롤 막기
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Alt+↑/↓ · Alt(Ctrl)+PgUp/PgDn — 이전·다음 인터페이스 (같은 칸 자리로), 지금 탭은 탭 줄 가운데로
  const cellRef = useRef<SheetCell | null>(null)
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null)
  const { barRef: tabBarRef, startCell, select: selectTab } = useSheetTabs({
    keys: machines.map(m => m.key), activeKey: active?.key ?? null, setActiveKey,
    blocked: () => adding || renaming != null,
    getCell: () => cellRef.current,
  })

  const setMachine = (key: string, update: (m: SpecMachine) => SpecMachine) =>
    setDraft(d => ({ ...d, machines: d.machines.map(m => (m.key === key ? update(m) : m)) }))
  const setItems = (key: string, items: SpecMachineItem[]) => setMachine(key, m => ({ ...m, items }))
  // 인터페이스 ID 를 바꾸면 전송코드 앞부분도 (인터페이스 시트와 같게)
  const renameMachine = (m: SpecMachine, code: string) =>
    setMachine(m.key, x => ({ ...x, code, items: x.items.map(i => ({ ...i, code: renamePrefix(i.code, x.code, code) })) }))

  const info = active ? issues.machine[active.key] : undefined

  const gridHandle = useRef<ItemGridHandle | null>(null)
  // 탭 조작 — 추가·복제(항목·태그 연결 포함, 인터페이스 시트의 복제와 같다)·순서·삭제
  const idRef = useRef<HTMLInputElement>(null)
  const setMachines = (update: (ms: SpecMachine[]) => SpecMachine[]) => setDraft(d => ({ ...d, machines: update(d.machines) }))
  const addMachine = () => {
    const m = blankMachine(newKey())
    setMachines(ms => [...ms, m])
    setActiveKey(m.key)
    setTimeout(() => idRef.current?.focus(), 50) // 새 인터페이스는 ID 부터
  }
  const duplicateMachine = (key: string) => {
    const i = machines.findIndex(m => m.key === key)
    if (i < 0) return
    const copy = { ...machines[i], key: newKey(), items: machines[i].items.map(it => ({ ...it, key: newKey() })) }
    setMachines(ms => [...ms.slice(0, i + 1), copy, ...ms.slice(i + 1)])
    setActiveKey(copy.key)
    onNotice('복제했습니다. 인터페이스 ID 를 바꾸면 전송코드 앞부분도 따라 바뀝니다.')
  }
  const removeMachine = (key: string) => {
    const i = machines.findIndex(m => m.key === key)
    const m = machines[i]
    if (!m) return
    if (m.items.length > 0 && !confirm(`'${m.code || '인터페이스'}' 와 항목 ${m.items.length}개를 지울까요? (Ctrl+Z 로 되돌릴 수 있습니다)`)) return
    setMachines(ms => ms.filter(x => x.key !== key))
    if (key === active?.key) setActiveKey(machines[i + 1]?.key ?? machines[i - 1]?.key ?? null)
  }

  // 검색 결과로 열었으면 그 칸으로 (표가 시트 높이에 맞춰 다시 그려진 뒤)
  useEffect(() => {
    if (!start) return
    const t = setTimeout(() => gridHandle.current?.focusField(start.row, start.field), 80)
    return () => clearTimeout(t)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [note, setNote] = useState<CellNote | null>(null)

  // 찾기/바꾸기 — 여는 동안 표에서 커서를 빼 두고 닫으면 돌려놓는다. 여러 칸을 고른 채 열면 그 영역만 바꿀 수 있다
  const resumeGrid = useRef<(() => void) | null>(null)
  const [finding, setFinding] = useState(false)
  const [findFocus, setFindFocus] = useState(0)
  const [findSel, setFindSel] = useState<FindSelection | null>(null)
  const [findMode, setFindMode] = useState<FindMode>('find')
  const openFind = (mode: FindMode = 'find') => {
    if (!resumeGrid.current) {
      const h = gridHandle.current
      setFindSel(h ? toFindSelection(active?.key, h.getSelection(), h.fieldAt) : null)
      resumeGrid.current = h?.release() ?? null
    }
    setFinding(true)
    setFindMode(mode)
    setFindFocus(n => n + 1)
  }
  useEffect(() => {
    if (finding || !resumeGrid.current) return
    resumeGrid.current()
    resumeGrid.current = null
  }, [finding])
  const openFindRef = useRef(openFind)
  const addingRef = useRef(adding)
  useEffect(() => { openFindRef.current = openFind; addingRef.current = adding })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase()
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || (k !== 'f' && k !== 'h') || addingRef.current) return
      e.preventDefault() // 브라우저 찾기·방문 기록 대신
      openFindRef.current(k === 'h' ? 'replace' : 'find')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const findScopes = [
    { key: 'tab', label: '이 인터페이스' }, { key: 'all', label: '모든 인터페이스' },
    ...(findSel ? [{ key: 'sel', label: selectionLabel(findSel) }] : []),
  ]
  const findRange = (scope: string, column: string) => {
    const sel = scope === 'sel' ? findSel : null
    const fields = (column === 'all' ? ITEM_TEXT : [column as ItemText]).filter(f => !sel || sel.fields.includes(f))
    const sheet = sel ? sel.sheetKey : active?.key
    return {
      fields,
      machineIn: (m: SpecMachine) => scope === 'all' || m.key === sheet,
      rowIn: (i: number) => !sel || (i >= sel.rows[0] && i <= sel.rows[1]),
    }
  }
  const findSearch = (pattern: RegExp, scope: string, column: string) => {
    const { fields, machineIn, rowIn } = findRange(scope, column)
    const hits: FindHit[] = []
    for (const m of machines.filter(machineIn)) m.items.forEach((item, row) => {
      if (rowIn(row)) for (const field of fields) {
        const count = item[field].match(pattern)?.length ?? 0
        if (count) hits.push({ sheet: m.key, row, field, count })
      }
    })
    return hits
  }
  // only — [바꾸기] 로 그 칸만
  const findApply = (pattern: RegExp, replacement: string, scope: string, column: string, only?: FindHit) => {
    const { fields, machineIn, rowIn } = findRange(scope, column)
    const count = (only ? [only] : findSearch(pattern, scope, column)).reduce((n, h) => n + h.count, 0)
    setDraft(d => ({
      ...d, machines: d.machines.map(m => (machineIn(m) && (!only || m.key === only.sheet)
        ? {
          ...m, items: m.items.map((item, i) => {
            if (!rowIn(i) || (only && i !== only.row)) return item
            let next = item
            for (const f of fields) {
              if (only && f !== only.field) continue
              const after = next[f].replace(pattern, () => replacement)
              if (after !== next[f]) next = { ...next, [f]: f === 'address' ? after.trim() : after }
            }
            return next
          }),
        }
        : m)),
    }))
    if (!only) onNotice(`${count}곳을 바꿨습니다. (Ctrl+Z 로 되돌리기)`)
  }
  // 찾은 칸으로 — 다른 인터페이스면 탭을 옮기고 표가 그려진 뒤
  const findGo = (hit: FindHit) => {
    if (hit.sheet === active?.key) return gridHandle.current?.focusField(hit.row, hit.field)
    setActiveKey(hit.sheet)
    setTimeout(() => gridHandle.current?.focusField(hit.row, hit.field), 80)
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-canvas">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-surface border-b border-gray-200 shrink-0">
        <button onClick={onClose} title="항목 편집 나가기 (Ctrl+Shift+E)" className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-100">
          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}><path d="M10 3 5 8l5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          나가기
        </button>
        <span className="text-sm font-semibold text-gray-800 truncate max-w-[220px] ml-1">{draft.name || '이름 없는 명세서'}</span>
        <span className="text-xs text-gray-500">마스터데이터 항목</span>
        <span className="ml-2"><SaveBadge status={spec.status} savedAt={spec.savedAt} error={spec.errorMessage} onRetry={spec.saveNow} /></span>
        <span className="ml-2"><ViewerAvatars viewers={viewers} me={me} /></span>
        <button onClick={spec.undo} disabled={!spec.canUndo} title="되돌리기 (Ctrl+Z)" className="ml-2 px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-30">↶ 되돌리기</button>
        <button onClick={spec.redo} disabled={!spec.canRedo} title="다시 실행 (Ctrl+Y)" className="px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-30">↷ 다시 실행</button>
        <div className="relative">
          <button onClick={() => (finding ? setFinding(false) : openFind())} onMouseDown={e => e.stopPropagation()}
            title="찾기 (Ctrl+F) / 바꾸기 (Ctrl+H) — 여러 칸을 고른 채 열면 그 영역만"
            className={`px-2 py-1 rounded text-xs ${finding ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'text-gray-600 hover:bg-gray-100'}`}>찾기/바꾸기</button>
          {finding && (
            <FindReplace key={findSel ? 'sel' : 'tab'} mode={findMode} onMode={setFindMode} scopes={findScopes} columns={FIND_COLUMNS} initialScope={findSel ? 'sel' : 'tab'}
              focusKey={findFocus} activeSheet={active?.key ?? null} search={findSearch} onReplace={findApply} onGo={findGo}
              onClose={() => setFinding(false)} />
          )}
        </div>
        <span className="ml-auto text-xs text-gray-500">
          인터페이스 {machines.length}
          {issues.dupPlaces.length > 0 && <> · <DupCodes places={issues.dupPlaces} activeKey={active?.key} align="right" onPick={setActiveKey} /></>}
        </span>
      </div>

      {active && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-200 bg-surface/60 shrink-0">
          <button onClick={() => setAdding(true)} title="디바이스의 태그를 골라 한 번에 추가합니다"
            className="bg-blue-600 text-white px-2.5 py-1 rounded-md text-xs font-medium hover:bg-accent-hover">태그 불러오기</button>
          <label className="flex items-center gap-1">인터페이스 ID
            <IdInput ref={idRef} key={active.key} value={active.code} onCommit={code => renameMachine(active, code)} />
          </label>
          <span className="text-gray-700">{active.description}</span>
          {info && <span>전송코드 {info.items}{info.unbound > 0 && ` · 태그 미연결 ${info.unbound}`}{info.missing > 0 && <span className="text-red-600 font-medium"> · 명세에 없음 {info.missing}</span>}{info.dup > 0 && <span className="text-amber-700" title="노랗게 표시된 전송코드 칸"> · 코드 겹침 {info.dup}</span>}</span>}
          <button onClick={() => { const items = fillFromTags(active, lookup); if (items !== active.items) setItems(active.key, items) }}
            title="전송코드가 비어 있는 항목을 태그의 Alias로 채웁니다"
            className="px-2 py-0.5 rounded text-accent hover:bg-blue-50">빈 전송코드 채우기</button>
          {/* 탭 숫자 색 범례 */}
          <span className="ml-auto flex items-center gap-2.5 text-gray-500" title="아래 탭의 숫자 = 전송코드 수, 점 색 = 태그 연결 상태">
            탭 숫자
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-600" />모두 연결</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-gray-400" />미연결 있음</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />명세에 없음</span>
          </span>
        </div>
      )}

      <div ref={sheetRef} className="flex-1 min-h-0 mx-3 mt-3 bg-surface rounded-t-lg border border-b-0 border-gray-200 overflow-hidden">
        {active ? (
          <ItemGrid key={active.key} machine={active} devices={draft.devices} lookup={lookup} dupCodes={issues.dupCodes}
            remote={remote} height={sheetHeight} startCell={startCell} onCell={cell => { cellRef.current = cell }}
            onChange={items => setItems(active.key, items)}
            onNote={setNote}
            handleRef={h => { gridHandle.current = h }}
            onLocate={(row, field, editing) => locate({ mode: 'items', device: active.key, tag: row, field, editing })} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
            인터페이스가 없습니다.
            <button onClick={addMachine} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover">+ 인터페이스 추가</button>
          </div>
        )}
      </div>
      {active && (
        <SheetStatus note={note} legend={['invalid', 'dup']}
          hint="태그를 고르면 전송코드가 {인터페이스 ID}_{Alias} 로 채워집니다 · Ctrl을 누른 채 끌면 번호가 1씩 늘어납니다" />
      )}

      {adding && active && (
        <AddTagsDialog machine={active} devices={draft.devices} onClose={() => setAdding(false)}
          onAdd={items => { setItems(active.key, [...active.items, ...items]); setAdding(false) }} />
      )}

      <div ref={tabBarRef} className="relative flex items-stretch gap-0.5 px-3 bg-gray-100 border-t border-gray-200 shrink-0 overflow-x-auto [scrollbar-width:none] h-9">
        {machines.map((m, i) => {
          const n = issues.machine[m.key]
          return (
            <div key={m.key} data-tab={m.key} draggable={renaming !== m.key} title={[m.name, m.description].filter(Boolean).join(' · ') || undefined}
              onDragStart={e => { e.dataTransfer.setData('text/machine', m.key); e.dataTransfer.effectAllowed = 'move' }}
              onDragOver={e => { if (e.dataTransfer.types.includes('text/machine')) e.preventDefault() }}
              onDrop={e => { const key = e.dataTransfer.getData('text/machine'); if (key) setMachines(ms => moveDevice(ms, key, i)) }}
              onClick={() => selectTab(m.key)}
              onDoubleClick={() => setRenaming(m.key)}
              onContextMenu={e => { e.preventDefault(); setMenu({ key: m.key, x: e.clientX, y: e.clientY }) }}
              className={`flex items-center px-3 text-xs whitespace-nowrap cursor-pointer select-none border-x border-b-2 ${
                m.key === active?.key ? 'bg-surface text-gray-900 font-medium border-gray-200 border-b-accent -mt-px' : 'text-gray-600 border-transparent hover:bg-gray-50'
              }`}>
              {renaming === m.key ? (
                <input autoFocus defaultValue={m.code} placeholder="인터페이스 ID" onClick={e => e.stopPropagation()}
                  onBlur={e => { const code = e.target.value.trim(); if (code !== m.code) renameMachine(m, code); setRenaming(null) }}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenaming(null) }}
                  className="w-28 border border-blue-400 rounded px-1 py-0.5 text-xs font-mono outline-none bg-surface" />
              ) : (
                <>
                  <span className="font-mono">{m.code || `인터페이스 ${i + 1}`}</span>
                  {m.name?.trim() && <span className="ml-1.5 font-normal text-gray-500">{m.name.trim()}</span>}
                  <span className={`ml-1.5 flex items-center gap-1 font-normal ${n?.missing ? 'text-red-600' : n?.unbound ? 'text-gray-500' : 'text-green-700'}`}
                    title={n?.missing ? `명세에 없는 태그 ${n.missing}` : n?.unbound ? `태그 미연결 ${n.unbound}` : '모두 연결됨'}>
                    <span className={`w-1.5 h-1.5 rounded-full ${n?.missing ? 'bg-red-500' : n?.unbound ? 'bg-gray-400' : 'bg-green-600'}`} />
                    {n?.items ?? 0}
                  </span>
                  <span className="ml-1.5 empty:hidden"><ViewerChips compact viewers={others.filter(v => v.cursor?.device === m.key && v.cursor.mode === 'items')} /></span>
                </>
              )}
            </div>
          )
        })}
        <button onClick={addMachine} title="인터페이스 추가" className="px-3 text-gray-500 hover:text-gray-800 hover:bg-gray-50 text-sm">+</button>
      </div>

      {menu && (
        <TabMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)} items={[
          { label: 'ID 바꾸기', action: () => setRenaming(menu.key) },
          { label: '복제 (항목·태그 포함)', action: () => duplicateMachine(menu.key) },
          { label: '왼쪽으로', action: () => setMachines(ms => moveDevice(ms, menu.key, ms.findIndex(x => x.key === menu.key) - 1)) },
          { label: '오른쪽으로', action: () => setMachines(ms => moveDevice(ms, menu.key, ms.findIndex(x => x.key === menu.key) + 1)) },
          { label: '삭제', action: () => removeMachine(menu.key), danger: true },
        ]} />
      )}
    </div>
  )
}

// 인터페이스 ID 칸 — 입력하는 동안은 내 글자, 칸을 벗어나면(Enter) 확정(Esc 는 취소). 입력 중이 아닐 때 다른 사람이 바꾸면 그 값으로 바뀐다
function IdInput({ ref, value, onCommit }: { ref?: React.Ref<HTMLInputElement>; value: string; onCommit: (code: string) => void }) {
  const [text, setText] = useState(value)
  const [editing, setEditing] = useState(false)
  const [prev, setPrev] = useState(value)
  if (value !== prev) { setPrev(value); if (!editing) setText(value) } // 렌더 중에 맞춘다 (effect 로 따라가면 한 번 늦다)
  const cancelled = useRef(false)
  return (
    <input ref={ref} value={text} onChange={e => setText(e.target.value)} placeholder="ID"
      onFocus={() => { setEditing(true); cancelled.current = false }}
      onBlur={() => {
        setEditing(false)
        const code = text.trim()
        if (!cancelled.current && code !== value) onCommit(code)
        else setText(value)
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur()
        if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur() }
      }}
      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs font-mono w-28 bg-surface" />
  )
}
